import React, { useContext, useMemo } from 'react';

import classNames from 'classnames';
import { useFormikContext } from 'formik';

import { SCHEDULE_NOT_LINKED_TO_TARGET, floatLabel, isCriticalEntry, scheduleEntries, targetName } from '../schedule/scheduleEntries';

import { startIsClear } from './earliestStart';
import useEarliestStart from './useEarliestStart';

import { formatDateLong } from 'Helpers/dateFormat';

import ProjectPlanContext from 'Context/project_plan/ProjectPlanContext';

const TAG_CLASS_NAME = 'tw-inline-block tw-rounded-md tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium';

const Tile = ({ label, className, children }) => (
    <div className={classNames('tw-rounded-lg tw-px-4 tw-py-3.5', className || 'tw-bg-charcoal-10')}>
        <span className="tw-mb-1 tw-block tw-text-xs tw-text-charcoal-75">{label}</span>
        {children}
    </div>
);

const DASH_CLASS_NAME = 'tw-text-xl tw-font-semibold tw-text-charcoal-50';

const Unknown = () => (
    <div className={DASH_CLASS_NAME} role="img" aria-label={$t('Not available')}>
        —
    </div>
);

const FloatTile = ({ entry, hasTarget }) => {
    const label = floatLabel(entry, hasTarget);
    const critical = isCriticalEntry(entry);
    let valueClassName = 'tw-text-xl tw-font-semibold';

    if (label?.ariaLabel) {
        valueClassName = DASH_CLASS_NAME;
    } else if (label?.muted) {
        valueClassName = 'tw-text-base tw-text-charcoal-60';
    }

    return (
        <Tile label={$t('Float')} className={critical ? 'tw-bg-red-10' : null}>
            {label ? (
                <div
                    className={classNames('tw-truncate', valueClassName, critical && 'tw-text-red')}
                    title={label.ariaLabel ? undefined : label.text}
                    role={label.ariaLabel ? 'img' : undefined}
                    aria-label={label.ariaLabel}
                >
                    {label.text}
                </div>
            ) : (
                <Unknown />
            )}
        </Tile>
    );
};

const CriticalPathTile = ({ entry, target }) => {
    let value = <Unknown />;

    if (isCriticalEntry(entry)) {
        value = <span className={classNames(TAG_CLASS_NAME, 'tw-bg-red-10 tw-text-red')}>{$t('On the critical path to {{target}}', { target })}</span>;
    } else if (entry) {
        value = <span className={classNames(TAG_CLASS_NAME, 'tw-bg-charcoal-15 tw-text-charcoal-75')}>{$t('Not critical')}</span>;
    }

    return (
        <Tile label={$t('Critical path')}>
            <div className="tw-text-sm">{value}</div>
        </Tile>
    );
};

const EarliestStartTile = ({ maskedPredecessors }) => {
    const { completenessStatuses } = useContext(ProjectPlanContext);
    const { values } = useFormikContext();

    const earliestStart = useEarliestStart(maskedPredecessors);
    const isComplete = parseInt(values.status) === parseInt(completenessStatuses.complete);
    const clear = !!earliestStart && startIsClear(values.start_date, earliestStart, isComplete);

    return (
        <Tile label={$t('Earliest start allowed')} className={clear ? 'tw-bg-green-10' : null}>
            <div className={classNames('tw-text-base tw-font-semibold', { 'tw-text-dark-green': clear })}>
                {earliestStart ? formatDateLong(earliestStart) : $t('No constraint')}
            </div>
        </Tile>
    );
};

/**
 * Float and critical path come from the last saved schedule; the earliest start follows the
 * predecessors as they are staged. A new activity has no schedule entry, so only its earliest start shows.
 */
const ActivitySummaryStrip = ({ activityId, maskedPredecessors }) => {
    const { schedule, scheduleFailed } = useContext(ProjectPlanContext);

    const entries = useMemo(() => scheduleEntries(schedule), [schedule]);
    let entry = activityId && !scheduleFailed ? entries.get(Number(activityId)) : undefined;

    // Without a target the schedule leaves out every activity that is neither complete nor unlinked.
    if (activityId && schedule && !scheduleFailed && !schedule.target_id && !entry) {
        entry = { state: SCHEDULE_NOT_LINKED_TO_TARGET };
    }

    return (
        <div className="tw-grid tw-grid-cols-3 tw-gap-3">
            {!!activityId && (
                <>
                    <FloatTile entry={entry} hasTarget={!!schedule?.target_id} />
                    <CriticalPathTile entry={entry} target={targetName(schedule)} />
                </>
            )}
            <EarliestStartTile maskedPredecessors={maskedPredecessors} />
        </div>
    );
};

export default ActivitySummaryStrip;
